/**
 * React Query hooks for Task CRUD operations.
 * Uses tRPC client for type-safe API calls.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import type { Task } from "../types";

// List tasks, with optional filters matching the API
export function useTasks(filters?: {
  groupId?: string;
  assigneeId?: string;
  status?: string;
  taskType?: string;
}) {
  return useQuery({
    queryKey: ["tasks", "list", filters],
    queryFn: () => trpc.task.list.query(filters || {}),
  }) as { data: Task[]; isLoading: boolean; isError: boolean; error: any };
}

// Get a single task by ID
export function useTask(taskId: string) {
  return useQuery({
    queryKey: ["tasks", "get", taskId],
    queryFn: () => trpc.task.get.query(taskId),
    enabled: !!taskId,
  }) as { data: Task | undefined; isLoading: boolean; isError: boolean; error: any };
}

// Create a new task
export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      title: string;
      description?: string;
      status?: string;
      assigneeId?: string;
      groupId?: string;
      dueAt: string | Date;
      objectId?: string;
      objectType?: string;
      taskType?: string;
    }) => {
      return await trpc.task.create.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

// Update an existing task
export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<Task>) => {
      return await trpc.task.update.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

// Delete a task
export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await trpc.task.delete.mutate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}